import classnames from 'classnames';
import React from 'react';
import { objectUtils } from '@codametrix/ui-common';
import { highlighterUtils } from '@codametrix/ui-common';

// ---------------------------------------
// types
// ---------------------------------------
export type HighlightedContentProps = {
  spans: CMxArtifacts.UnstructuredSpan[];
  segments: CMxAPI.ContentSegment[];
  id: any;
};

type UnhighlightedText = {
  segment?: React.ReactElement;
  advanced: number;
};

// counter function to generate unique keys.  there are currently no
// unique keys on segments or spans.  It's not clear to me that we'd use
// them as keys anyways, as we're creating "synthetic" spans in places.
// So, the proposal for now is just to use a counter to generate an
// increasing number.
type Counter = () => string;
const counterGenerator = (id: any) => {
  let idx = 0;
  return () => `{id}-${idx++}`;
};

/**
 * Returns a portion of text designed to be displayed without highlighting.
 * If no text was selected, the return's advanced property will be 0.
 * @param span
 * @param segment
 * @param currentIndex
 */
export const getUnhighlightedText = (
  span: CMxArtifacts.UnstructuredSpan,
  segment: CMxAPI.ContentSegment,
  currentIndex: number,
  counter: Counter
): UnhighlightedText => {
  const firstSpanStarts = span.span.start;
  const sliceLength = firstSpanStarts - segment.start - currentIndex;
  if (sliceLength) {
    const slice = segment.text.slice(currentIndex, currentIndex + sliceLength);
    return {
      segment: unhighlightedText(slice, counter),
      advanced: sliceLength
    };
  }
  return { advanced: 0 };
};

// ---------------------------------------
// React based code
// ---------------------------------------
const unhighlightedText = (text: string, counter: Counter) => {
  if (objectUtils.isString(text) && text.trim().length === 0) {
    return emptySegment({ key: `span-${counter()}` });
  }

  return (
    <span className="unhighlighted qa-unhighlighted" key={`span-${counter()}`}>
      {text}
    </span>
  );
};

const highlightText = (
  text: string | React.ReactChild[],
  span: CMxArtifacts.UnstructuredSpan,
  counter: Counter
) => {
  const hasContent =
    objectUtils.isString(text) && (text as string).trim().length > 0;
  const content = objectUtils.isString(text) && !hasContent ? <br /> : text;
  //@ts-ignore
  const provenance = (span.code?.provenance ?? '').toLowerCase();
  const classNames = [
    { 'highlighted-segment': hasContent, 'empty-block': !hasContent },
    `qa-highlighted`,
    `highlighted-${span.codeType.toLowerCase()}`,
    `highlighted-${provenance}`,
    span.span.uuid ? `span-${span.span.uuid}` : false,
    // hashes are not currently
    span.span.hash ? `span-${span.span.hash}` : false,
    { 'highlighted-active': span.active, 'qa-highlighted-active': span.active }
  ];
  return (
    <span
      id={span.span.uuid}
      className={classnames(classNames)}
      key={`span-${counter()}`}>
      {content}
    </span>
  );
};

type ContentType = React.ReactElement | string;
/**
 * Given a segment, apply all the evidence to it.
 * @param segment
 * @param spans
 */
const highlightSegment = (
  segment: CMxAPI.ContentSegment,
  spans: CMxArtifacts.UnstructuredSpan[],
  counter: Counter
) => {
  const segments: ContentType[] = [];
  let currentIndex = 0;
  const seen = new Set<CMxArtifacts.UnstructuredSpan>();

  spans.forEach((span, idx) => {
    if (!seen.has(span)) {
      // if the span starts later than the segment, let's peel off first chunk of text
      const unhighlighted = getUnhighlightedText(
        span,
        segment,
        currentIndex,
        counter
      );
      if (unhighlighted.segment) {
        segments.push(unhighlighted.segment);
        // advance along the string length n units
        currentIndex += unhighlighted.advanced;
      }

      // determine if there's any nesting.
      const subsections = spans.slice(idx);
      const nest = highlighterUtils.buildNest(subsections, segment);
      let text: string = segment.text.slice(nest.start, nest.end);
      // Note: spans operate on a relative basis.
      // span.start = 53 may mean that the span is only
      // starting 3 characters in if the span starts at character
      // 50; as would be the case with a segment that starts at 50.
      // text slicing on the other hand, operates on absolute basis.
      if (!nest.hasChildren) {
        segments.push(highlightText(text, span, counter));
      } else {
        // 1. create psuedo segment
        const start = segment.start + currentIndex;
        const end = start + text.length;
        const psuedoSegment = {
          text: text,
          start: start,
          end: end
        };
        // 2. recurse through data structure
        const highlightedSubsegments = highlightSegment(
          psuedoSegment,
          nest.children,
          counter
        );
        nest.children.forEach(child => {
          seen.add(child);
        });
        // 3. wrap the output in another span
        segments.push(
          highlightText(highlightedSubsegments, nest.parent, counter)
        );
      }

      currentIndex += text.length;
    }
  });
  // if there are any leftovers, append the raw string.s
  const leftoverText = segment.text.slice(currentIndex);
  if (leftoverText.length) {
    segments.push(unhighlightedText(leftoverText, counter));
  }

  if (!segments.length) {
    let emptyKey = counter();
    segments.push(
      emptySegment({ className: `span-no-segments-${emptyKey}`, key: emptyKey })
    );
  }
  return segments;
};

const emptySegment = (props?: any) => {
  const classes = [props.className, 'empty-segment'];
  return (
    <div {...props} className={classnames(classes)}>
      <br />
    </div>
  );
};

const highlightSegments = (
  segments: CMxAPI.ContentSegment[],
  spans: CMxArtifacts.UnstructuredSpan[],
  counter: Counter
) => {
  const tempSpans = [...spans];
  const texts = [];

  let currentSegment = 0;
  const totalSegments = segments.length;

  while (currentSegment < totalSegments) {
    const activeSegment = segments[currentSegment];
    const activeSpans = highlighterUtils.getActiveSpans(
      tempSpans,
      activeSegment
    );
    const textContents = highlightSegment(activeSegment, activeSpans, counter);

    texts.push(
      <div className="content-segment" key={`content-segment-${counter()}`}>
        <span className="highlighter-diagnostics">
          {activeSegment.start} - {activeSegment.end}
        </span>
        {textContents}
      </div>
    );
    currentSegment++;
  }
  return <div className="highlighted-content">{texts}</div>;
};

const HighlightedContent = (props: HighlightedContentProps) => {
  const { spans, segments, id } = props;

  const counter = counterGenerator(id);
  const texts = highlightSegments(segments, spans, counter);

  return <div className="highlighted-content">{texts}</div>;
};

export { HighlightedContent };
